import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext(null);

// Feature names matching backend
export const Features = {
  PROFILE_MANAGEMENT: 'profile_management',
  VIEW_JOB_MATCHES: 'view_job_matches',
  BASIC_VFIT_SCORES: 'basic_vfit_scores',
  SUPPORT_TICKETS: 'support_tickets',
  AI_RESUME_PARSING: 'ai_resume_parsing',
  CAREER_INSIGHTS: 'career_insights',
  AI_CAREER_PATHWAYS: 'ai_career_pathways',
  ADVANCED_MATCH_FILTERS: 'advanced_match_filters',
  PRIORITY_MATCHING: 'priority_matching',
  EXPORT_DATA: 'export_data',
};

export const SubscriptionProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradePrompts, setUpgradePrompts] = useState({});

  const fetchSubscription = useCallback(async () => {
    if (!token || !user) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/candidates/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      // Default to launch tier if fetch fails (graceful degradation)
      setSubscription({
        subscription_tier: 'launch',
        is_launch_user: true,
        has_premium_access: true,
        features: Object.fromEntries(Object.values(Features).map(f => [f, true])),
      });
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  const fetchUpgradePrompts = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API}/api/candidates/subscription/upgrade-prompts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpgradePrompts(response.data.prompts);
    } catch (error) {
      console.error('Failed to fetch upgrade prompts:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchSubscription();
    fetchUpgradePrompts();
  }, [fetchSubscription, fetchUpgradePrompts]);

  /**
   * Check if user can access a feature
   * @param {string} feature - Feature name from Features enum
   * @returns {boolean}
   */
  const canAccess = (feature) => {
    if (!subscription) return true; // Default to allowing during load
    return subscription.features?.[feature] ?? true;
  };

  /**
   * Check if user has premium access (launch or pro tier)
   * @returns {boolean}
   */
  const hasPremiumAccess = () => {
    if (!subscription) return true;
    return subscription.has_premium_access ?? true;
  };

  /**
   * Get the current subscription tier
   * @returns {string} 'launch' | 'free' | 'pro'
   */
  const getTier = () => {
    return subscription?.subscription_tier ?? 'launch';
  };

  /**
   * Check if user is a grandfathered launch user
   * @returns {boolean}
   */
  const isLaunchUser = () => {
    return subscription?.is_launch_user ?? true;
  };

  /**
   * Get upgrade prompt for a feature
   * @param {string} feature - Feature name
   * @returns {object} { title, description, cta }
   */
  const getUpgradePrompt = (feature) => {
    return upgradePrompts[feature] || {
      title: 'Upgrade to Pro',
      description: 'Unlock this premium feature with a Pro subscription.',
      cta: 'Upgrade to Pro',
    };
  };

  /**
   * Get tier display info
   * @returns {object} { name, description, badge_color, icon }
   */
  const getTierDisplay = () => {
    return subscription?.tier_display || {
      name: 'Launch Member',
      description: 'Early adopter with full access',
      badge_color: 'purple',
      icon: 'rocket',
    };
  };

  const value = {
    subscription,
    loading,
    canAccess,
    hasPremiumAccess,
    getTier,
    isLaunchUser,
    getUpgradePrompt,
    getTierDisplay,
    refreshSubscription: fetchSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
